import React, { useRef } from "react";
import { motion } from "framer-motion";

// Track geometry (percent positions inside the watercolor course scene)
const TEE = 15;
const HOLE = 83;
const FLAG_X = 82.9; // pole position (%) in the base scene
const FLAG_Y = -13; // pole-top position (%)
const HOLE_X = 83.1; // black cup centre X (%) in the base scene
const HOLE_Y = 41.2; // black cup centre Y (%) in the base scene
// Tee, four checkpoints (last one at the fairway's end), then the hole
const STOP_POS = [TEE, 28.75, 42.5, 56.25, 70, HOLE];

const shortMoney = (n) => {
  if (n >= 1_000_000) return `$${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
};

const ballPosition = (total, stops) => {
  let k = 0;
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (total >= stops[i]) k = i;
  }
  if (k >= stops.length - 1) return HOLE;
  const frac = Math.min(1, Math.max(0, (total - stops[k]) / (stops[k + 1] - stops[k])));
  return STOP_POS[k] + frac * (STOP_POS[k + 1] - STOP_POS[k]);
};

// Waypoint burgee (matches the Million Dollar Shot pin flag) on a slim pole with
// a little gold finial and a soft painted cast shadow — alternating gold / cream.
const Pennant = ({ gold }) => (
  <svg width="30" height="48" viewBox="0 0 26 44" className="overflow-visible drop-shadow-[0_2px_3px_rgba(40,50,30,0.32)]">
    <defs>
      <linearGradient id="pnG" x1="0" y1="0" x2="1" y2="0.65">
        <stop offset="0%" stopColor="#EACB7C" />
        <stop offset="55%" stopColor="#C9A24C" />
        <stop offset="100%" stopColor="#A67E20" />
      </linearGradient>
      <linearGradient id="pnC" x1="0" y1="0" x2="1" y2="0.65">
        <stop offset="0%" stopColor="#FFFDF6" />
        <stop offset="100%" stopColor="#E6DCC5" />
      </linearGradient>
    </defs>
    {/* painted ground shadow */}
    <ellipse cx="12.6" cy="42.6" rx="4.6" ry="1.3" fill="#33422a" opacity="0.3" />
    {/* slim pole */}
    <rect x="11.75" y="5" width="1.7" height="37.4" rx="0.85" fill="#2A2724" />
    {/* gold finial */}
    <circle cx="12.6" cy="4" r="2.1" fill="#D9B75E" stroke="#8C6D2A" strokeWidth="0.5" />
    <circle cx="11.95" cy="3.4" r="0.6" fill="#F6E6B0" opacity="0.9" />
    {/* swallowtail burgee */}
    <path
      d="M12.95 5.4 L25 7.6 L21.1 10.2 L25 12.8 L12.95 15 Z"
      fill={gold ? "url(#pnG)" : "url(#pnC)"}
      stroke={gold ? "#7D5F22" : "#B3A67D"}
      strokeWidth="0.6"
      strokeLinejoin="round"
    />
  </svg>
);

// Elevated golf-hole progress tracker painted in the watercolor style of the hero.
// The ball travels the fairway from the branded tee toward the flagged hole.
export const GolfProgress = ({ totalRaised, goal, pct, celebrating, fmt }) => {
  const checkpoints = [1, 2, 3, 4].map((i) => Math.round((goal * i) / 5));
  const stopValues = [0, ...checkpoints, goal];
  const ballPct = ballPosition(totalRaised, stopValues);
  const ballLeft = `${ballPct}%`;
  const goalMet = totalRaised >= goal && goal > 0;

  const rootRef = useRef(null);

  // Dotted shot-arc from the tee toward the ball; on goal it lands in the cup centre.
  // viewBox 320x100 maps uniformly onto the 1600x500 scene (x% * 3.2, y%).
  const endXpct = goalMet ? HOLE_X : ballPct;
  const endYpct = goalMet ? HOLE_Y : 50;
  const x1 = 14.6 * 3.2; // start at the tee base X (matches the tee emblem centre)
  const x2 = endXpct * 3.2;
  const y1 = 48; // tee base Y (tee emblem bottom sits at 48% from top)
  const y2 = endYpct;
  const dist = Math.max(0, x2 - x1);
  const arcH = Math.min(34, 8 + dist * 0.32);
  const arcPath = `M ${x1} ${y1} Q ${(x1 + x2) / 2} ${Math.min(y1, y2) - arcH} ${x2} ${y2}`;

  return (
    <div ref={rootRef} data-testid="golf-progress">
      {/* Visual golf-course tracker — desktop/tablet only (hidden on mobile phones) */}
      <div className="hidden mt-56 sm:mt-72 md:block">
        {/* Watercolour course */}
        <div className="relative isolate mx-auto w-full max-w-4xl">
        {/* Elegant gala crowd along the fairway (behind the grass), starry sky + market lights */}
        <img
          src="/brand/tracker-crowd.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 z-[-1] -translate-x-1/2 select-none"
          style={{ bottom: "40%", width: "92%" }}
          draggable={false}
        />
        {/* Twinkling stars (same as the hero) in the sky above the trees & clubhouse */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true" data-testid="tracker-twinkle-stars">
          {[
            { l: 8, t: -75, s: 2, d: 0.4, du: 4 },
            { l: 15, t: -80, s: 1.5, d: 1.9, du: 5 },
            { l: 22, t: -74, s: 2.5, d: 1.1, du: 4.5 },
            { l: 29, t: -81, s: 1.5, d: 2.6, du: 3.6 },
            { l: 36, t: -72, s: 2, d: 0, du: 4.2 },
            { l: 42, t: -78, s: 1.5, d: 1.5, du: 5 },
            { l: 48, t: -69, s: 2, d: 2.2, du: 3.8 },
            { l: 54, t: -80, s: 2.5, d: 0.7, du: 4.6 },
            { l: 60, t: -71, s: 1.5, d: 3.0, du: 4 },
            { l: 66, t: -77, s: 2, d: 1.3, du: 5 },
            { l: 72, t: -68, s: 1.5, d: 0.9, du: 3.7 },
            { l: 78, t: -79, s: 2, d: 2.4, du: 4.4 },
            { l: 84, t: -73, s: 1.5, d: 0.3, du: 5 },
            { l: 90, t: -78, s: 2, d: 1.7, du: 4 },
            { l: 12, t: -70, s: 1.5, d: 2.9, du: 4.8 },
            { l: 51, t: -75, s: 1.5, d: 3.3, du: 4 },
            { l: 69, t: -74, s: 2, d: 0.6, du: 4.5 },
            { l: 33, t: -68, s: 1.5, d: 1.2, du: 5 },
          ].map((st, i) => (
            <span
              key={i}
              className="star"
              style={{
                top: `${st.t}%`,
                left: `${st.l}%`,
                width: st.s,
                height: st.s,
                animationDelay: `${st.d}s`,
                animationDuration: `${st.du}s`,
                boxShadow: "0 0 5px 1px rgba(243,228,176,0.85)",
              }}
            />
          ))}
        </div>
        <img
          src="/brand/tracker-course.png"
          alt="Golf fairway from the tee to the hole"
          className="pointer-events-none w-full select-none drop-shadow-[0_10px_26px_rgba(59,71,45,0.16)]"
          draggable={false}
        />

        {/* Watercolor tee emblem — overlays the flat baked emblem at the tee */}
        <img
          src="/brand/tracker-tee.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute z-[2] select-none"
          style={{ left: "14.6%", bottom: "52%", width: "7.4%", transform: "translateX(-50%)" }}
          draggable={false}
        />

        {/* Celebration banner planted in the fairway once the goal is met.
            Layered behind the pins, checkpoints, ball and animated arc. */}
        {goalMet && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 select-none"
            style={{ bottom: "45%", width: "48%", left: "48%" }}
            data-testid="goal-banner"
          >
            <div className="relative">
              <img src="/brand/goal-banner.png" alt="Our Champions Made the Green — Let's Keep the Momentum Going" className="w-full select-none" draggable={false} />
            </div>
          </div>
        )}

        {/* Taller pin flagstick — thin painted pole with a soft highlight */}
        <div
          className="pointer-events-none absolute z-[5] rounded-full"
          style={{ left: "83.05%", top: "-14%", height: "56%", width: "3px", background: "linear-gradient(90deg, #1c1a17, #46403a 45%, #1c1a17)", transform: "translateX(-50%)" }}
        />

        {/* Million Dollar Shot pennant on the pin (static, wind-blown) */}
        <div
          className="pointer-events-none absolute z-20"
          style={{ left: `${FLAG_X}%`, top: `${FLAG_Y}%`, width: "9.5%" }}
        >
          <img src="/brand/mds-pin-flag.png" alt="Million Dollar Shot pennant" className="w-full select-none drop-shadow-[0_3px_5px_rgba(59,71,45,0.3)]" draggable={false} />
        </div>

        {/* Overlay layer aligned to the fairway centre line (50%) */}
        <div className="absolute inset-0">
          {/* Dotted shot arc: dots continually march toward the hole */}
          {dist > 4 && (
            <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 320 100" preserveAspectRatio="none" fill="none">
              <motion.path
                d={arcPath}
                stroke="#FBF3DE"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeDasharray="0.1 8.1"
                opacity="0.95"
                style={{ filter: "drop-shadow(0 1px 1px rgba(45,55,30,0.55))" }}
                animate={{ strokeDashoffset: [0, -8.2] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
              />
              {/* Alternating dot: slightly larger and gold, sitting between the white dots */}
              <motion.path
                d={arcPath}
                stroke="#C9A24C"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeDasharray="0.1 8.1"
                opacity="1"
                style={{ filter: "drop-shadow(0 1px 1px rgba(45,55,30,0.55))" }}
                animate={{ strokeDashoffset: [-4.1, -12.3] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
              />
            </svg>
          )}

          {/* Gold travelled trail — stops just before the "$" of the goal amount on completion */}
          <motion.div
            className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full"
            style={{ left: `${TEE}%`, background: "linear-gradient(90deg, rgba(201,162,76,0.35), #C9A24C)" }}
            initial={{ width: 0 }}
            animate={{ width: `calc(${goalMet ? 77.3 : ballPct}% - ${TEE}%)` }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Checkpoint pennants (gold), totals centered on the fairway line */}
          {checkpoints.map((m, idx) => (
            <div key={m} className="absolute" style={{ left: `${STOP_POS[idx + 1]}%`, top: "50%" }}>
              <div className="absolute -translate-x-1/2" style={{ left: 0, bottom: "1px" }}>
                <Pennant gold={idx % 2 === 0} />
              </div>
              <span
                className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-sans text-[11px] font-bold uppercase tracking-[0.08em] text-white"
                style={{ left: 0, top: 0, textShadow: "0 1px 3px rgba(40,50,30,0.95), 0 0 2px rgba(40,50,30,0.85)" }}
              >
                {fmt(m)}
              </span>
            </div>
          ))}

          {/* Golf ball travelling the fairway — disappears into the cup once the goal is met */}
          {!goalMet && (
          <motion.div
            className="absolute top-1/2 z-10"
            initial={{ left: `${TEE}%` }}
            animate={{ left: ballLeft }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transform: "translate(-50%,-50%)" }}
            data-testid="golf-progress-ball"
          >
            <motion.div animate={celebrating ? { y: [0, -12, 0] } : {}} transition={{ duration: 0.6, repeat: celebrating ? 3 : 0 }}>
              <svg width="34" height="40" viewBox="0 0 32 40" className="overflow-visible">
                <defs>
                  <radialGradient id="ballshade" cx="38%" cy="30%" r="72%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                    <stop offset="52%" stopColor="#ffffff" stopOpacity="0" />
                    <stop offset="100%" stopColor="#8f957f" stopOpacity="0.55" />
                  </radialGradient>
                </defs>
                {/* painted watercolour cast shadow on the grass */}
                <ellipse cx="16" cy="33" rx="9" ry="2.4" fill="#33422a" opacity="0.3" />
                {/* ball with soft ink outline */}
                <circle cx="16" cy="15" r="12" fill="#FBF6EA" stroke="#8d9379" strokeWidth="0.9" />
                <circle cx="16" cy="15" r="12" fill="url(#ballshade)" />
                {[[13, 11], [19, 12], [16, 15], [12, 17], [20, 17], [16, 20]].map(([cx, cy], i) => (
                  <circle key={i} cx={cx} cy={cy} r="1.05" fill="#c3c7b3" opacity="0.5" />
                ))}
              </svg>
            </motion.div>
          </motion.div>
          )}
        </div>

        {/* Goal amount inside the green, just below the hole */}
        <span
          className="absolute z-10 font-display font-bold text-mds-gold"
          style={{ left: "83.1%", top: "47%", transform: "translate(-50%,-50%)", fontSize: "clamp(18px, 3vw, 28px)", textShadow: "0 1px 2px rgba(40,50,30,0.5)" }}
        >
          {fmt(goal)}
        </span>
        </div>
      </div>

      {/* Raised summary — shows on all viewports; tucks under the tracker on desktop */}
      <div className="mt-8 text-center md:-mt-12">
        <p className={`font-display text-2xl text-mds-ink sm:text-3xl ${celebrating ? "total-shimmer" : ""}`} data-testid="champions-total-amount">
          Our Champions Have Raised <b><i>{fmt(totalRaised)}</i></b> of the <b><i>{fmt(goal)}</i></b> Goal
        </p>
        <p className="mt-2 font-display text-2xl italic text-mds-gold sm:text-3xl">
          <b>Thank You for Your Generosity!</b>
        </p>
      </div>
    </div>
  );
};
